import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, User, Loader2, ArrowLeft } from "lucide-react";
import { Mandala, Lotus } from "@/components/decor/Motifs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
    const nav = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { username, password });
            localStorage.setItem("iys_admin_token", data.token);
            localStorage.setItem("iys_admin_user", data.username);
            toast.success("Welcome, Prabhu. Access granted.");
            nav("/admin");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Invalid credentials.";
            toast.error(typeof msg === "string" ? msg : "Login failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="relative min-h-screen bg-inkbrown-900 flex items-center justify-center px-6 overflow-hidden">
            <Mandala className="absolute -top-40 -left-40 w-[520px] h-[520px] opacity-10 animate-spin-slower" stroke="#D4AF37" />
            <Mandala className="absolute -bottom-40 -right-40 w-[560px] h-[560px] opacity-10 animate-spin-slow" stroke="#FF9933" />

            <Link
                to="/"
                data-testid="admin-back-home-link"
                className="absolute top-6 left-6 inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.25em] text-vgold-200 hover:text-vgold-100 transition-colors"
            >
                <ArrowLeft className="w-4 h-4" /> Home
            </Link>

            <div className="relative w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="mx-auto w-16 h-16 rounded-full bg-saffron-500/15 border border-vgold-400/40 flex items-center justify-center">
                        <Lotus className="w-8 h-8" />
                    </div>
                    <p className="mt-6 font-body text-xs uppercase tracking-[0.35em] text-vgold-300">
                        Sacred Sanctum
                    </p>
                    <h1 className="mt-2 font-title text-4xl text-cream-50 tracking-wide">
                        Admin Access
                    </h1>
                </div>

                <form
                    onSubmit={submit}
                    className="glass-dark rounded-3xl p-8 space-y-6 shadow-goldring"
                    data-testid="admin-login-form"
                >
                    <div>
                        <label className="mb-2 font-body text-[10px] uppercase tracking-[0.3em] text-vgold-300 block">
                            Username
                        </label>
                        <div className="flex items-center gap-3 border-b-2 border-vgold-400/40 focus-within:border-saffron-500 py-2">
                            <User className="w-4 h-4 text-vgold-300" />
                            <input
                                type="text"
                                required
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                data-testid="admin-username-input"
                                className="flex-1 bg-transparent outline-none text-cream-50 font-body placeholder:text-cream-50/40"
                                placeholder="admin"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="mb-2 font-body text-[10px] uppercase tracking-[0.3em] text-vgold-300 block">
                            Password
                        </label>
                        <div className="flex items-center gap-3 border-b-2 border-vgold-400/40 focus-within:border-saffron-500 py-2">
                            <Lock className="w-4 h-4 text-vgold-300" />
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="admin-password-input"
                                className="flex-1 bg-transparent outline-none text-cream-50 font-body placeholder:text-cream-50/40"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit-btn"
                        className="w-full rounded-full bg-saffron-500 hover:bg-saffron-600 text-white font-body font-bold uppercase tracking-[0.25em] py-3 shadow-glow disabled:opacity-60 transition-all"
                    >
                        {submitting ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                            </span>
                        ) : (
                            "Enter"
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
